/**
 * The single rule for "what label to show for a person": their real `name` if set,
 * otherwise the always-present `username`. Use this everywhere a tutor/user is shown by
 * name (cards, banners, hero CTA) instead of re-writing `name ?? username` — the fallback
 * lives in one place so it can't drift.
 */
export function displayName(person: { name?: string | null; username: string }): string {
  return person.name?.trim() || person.username;
}

/**
 * The same label cut to what you'd call someone out loud: "Anna Garusova" → "Anna".
 * For copy that addresses a person rather than identifies them ("Meet Anna", "Sessions
 * with Anna"). A username has no parts to cut, so it comes back whole. Names that are
 * one word, or written family-name-first, come back as their first word either way —
 * this is a friendliness heuristic, not a name parser, so keep it out of anything
 * legal, structured (JSON-LD) or addressed to third parties.
 */
export function firstName(person: { name?: string | null; username: string }): string {
  return firstWord(displayName(person));
}

/** The same cut applied to a label that is already resolved — a name that has
    been through `displayName`, or one the caller holds as a plain string. */
export function firstWord(label: string): string {
  return label.split(' ')[0];
}
